/**
 * Permission codes the point-of-sale back office declares an interest in.
 *
 * These are the dot-strings `internal/pos/permissions.go` and
 * `internal/device/permissions.go` register with the RBAC catalogue
 * (CLAUDE.md §6) and that arrive on `/auth/me`. Nothing here is a security
 * boundary — the server re-checks every route and again in the service layer —
 * but an action a user cannot perform should not be rendered disabled, it
 * should not be rendered (§11).
 *
 * **Four segregation-of-duties pairs run through these screens and no screen
 * ever infers one half from the other** (C8 §6.1):
 *
 *  - `sessionCount` / `sessionClose` — whoever declares what is in the drawer
 *    must not be whoever accepts the difference. A blind count signed off by
 *    the counter is not a blind count.
 *  - `saleRefund` / `sessionClose` — the classic point-of-sale fraud. A refund
 *    takes cash out of the drawer and closes the gap a theft left; one person
 *    holding both can steal in the morning and reconcile in the evening.
 *  - `instrumentIssue` / `instrumentAdjust` — issuing a bearer liability and
 *    adjusting its balance is the store-credit form of create-versus-approve.
 *  - `deviceManage` / `contentPublish` — one person who can enrol a device and
 *    publish it a price set can enrol a terminal nobody knows about and sell
 *    from it at a price nobody approved.
 *
 * So the count sheet and the close action are separately permissioned surfaces,
 * and the fleet board never offers "enrol" and "publish" from one button.
 *
 * `moduleManage` on either module guards no route. It is the `<module>.manage`
 * fallback `core/rules` consults when no authority band covers an overridable
 * rule's doc type; without it a discount override returns
 * `ERR_OVERRIDE_NOT_AUTHORISED` on a tenant with no matrix. Both are listed so
 * a screen can explain a refusal rather than merely report one.
 *
 * The codes at the foot belong to other modules and are here because these
 * screens cannot do their job without them: the company an outlet belongs to,
 * the GL accounts POS settings map to, the journal a day close posts, the
 * warehouse a sale depletes and the operating day the close is a boundary of.
 */

export const POS_PERMISSIONS = {
  outletRead: 'pos.outlet.read',
  outletManage: 'pos.outlet.manage',
  tenderTypeManage: 'pos.tender_type.manage',

  menuRead: 'pos.menu.read',
  menuManage: 'pos.menu.manage',
  /** Publishing a menu version — deliberately not the same authority as editing it. */
  menuPublish: 'pos.menu.publish',
  availabilityManage: 'pos.availability.manage',
  allergenManage: 'pos.allergen.manage',

  saleRead: 'pos.sale.read',
  saleCreate: 'pos.sale.create',
  saleSettle: 'pos.sale.settle',
  saleVoid: 'pos.sale.void',
  /** SoD partner of `sessionClose`. */
  saleRefund: 'pos.sale.refund',
  discountApply: 'pos.discount.apply',
  priceOverride: 'pos.price.override',

  checkRead: 'pos.check.read',
  checkManage: 'pos.check.manage',
  checkTransfer: 'pos.check.transfer',

  kdsRead: 'pos.kds.read',
  kdsBump: 'pos.kds.bump',

  sessionRead: 'pos.cash_session.read',
  sessionOpen: 'pos.cash_session.open',
  /** SoD partner of `sessionClose`. Submitting the count. */
  sessionCount: 'pos.cash_session.count',
  /** SoD partner of `sessionCount` and of `saleRefund`. Accepting the variance. Step-up MFA. */
  sessionClose: 'pos.cash_session.close',
  cashMovement: 'pos.cash_movement.record',
  /** Step-up MFA. */
  depositManage: 'pos.deposit.manage',

  dayCloseRead: 'pos.day_close.read',
  /** Step-up MFA. Posting the day's journal. */
  dayClosePost: 'pos.day_close.post',

  instrumentRead: 'pos.instrument.read',
  /** SoD partner of `instrumentAdjust`. */
  instrumentIssue: 'pos.instrument.issue',
  /** SoD partner of `instrumentIssue`. Step-up MFA. */
  instrumentAdjust: 'pos.instrument.adjust',

  overrideAuthorise: 'pos.override.authorise',
  operatorManage: 'pos.operator.manage',

  anomalyRead: 'pos.anomaly.read',
  anomalyResolve: 'pos.anomaly.resolve',

  retailManage: 'pos.retail.manage',

  /** Guards no route: the `<module>.manage` fallback for rule overrides. */
  moduleManage: 'pos.manage',
} as const

export const DEVICE_PERMISSIONS = {
  deviceRead: 'device.device.read',
  /** SoD partner of `contentPublish`. Step-up MFA. */
  deviceManage: 'device.device.manage',

  contentRead: 'device.content.read',
  contentManage: 'device.content.manage',
  /** SoD partner of `deviceManage`. */
  contentPublish: 'device.content.publish',

  policyManage: 'device.policy.manage',
  /** Step-up MFA. */
  rosterPublish: 'device.roster.publish',

  syncRead: 'device.sync.read',
  /** Resolving a sync conflict. A conflict has an owner, never an approver. */
  conflictResolve: 'device.conflict.resolve',
  ratificationDecide: 'device.ratification.decide',

  /** Guards no route: the `<module>.manage` fallback for rule overrides. */
  moduleManage: 'device.manage',
} as const

/* ------------------------------------------------- other modules' codes */

export const POS_FOREIGN_PERMISSIONS = {
  companyRead: 'org.company.read',
  locationRead: 'org.location.read',
  accountRead: 'org.account.read',
  journalRead: 'gl.journal.read',
  bankAccountRead: 'cash.account.read',
  customerRead: 'ar.customer.read',
  employeeRead: 'hr.employee.read',
  warehouseRead: 'inventory.warehouse.read',
  itemRead: 'inventory.item.read',
  businessDayRead: 'opcal.day.read',
} as const

export type PosPermission = (typeof POS_PERMISSIONS)[keyof typeof POS_PERMISSIONS]
export type DevicePermission = (typeof DEVICE_PERMISSIONS)[keyof typeof DEVICE_PERMISSIONS]
